<script setup>
import { can } from '@/@layouts/plugins/casl'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Questionnaire from '@/views/survey/Questionnaire.vue'
import ScheduleDialog from '@/views/survey/ScheduleDialog.vue'
import SurveyService from '@/services/survey/survey.service'
import { SurveyType } from '@/enums/survey/survey.type'
import { useApi } from '@/composables/useApi'
import SurveyForm from '@/views/survey/setup/SurveyForm.vue'
import Schedule from '@/views/survey/setup/Schedule.vue'
import Participants from '@/views/survey/Participants.vue'
import ParticipantCondition from '@/views/survey/setup/ParticipantCondition.vue'

definePage({
  meta: {
    name: "Survey Setup",
  },
})

const route = useRoute()
const router = useRouter()

const surveyType = computed(() => route.params.type)
const surveyId = computed(() => route.params.survey ?? null)

const survey = ref()

// Validate Survey Type
if (!surveyType) {
  router.push({
    name: 'invalid-url',
    params: { code: 404 },
  })
}

if (surveyId.value) {
  await useApi(createUrl(`/survey/surveys/${surveyId.value}`), {
    beforeFetch: () => {},
    afterFetch: ({ data }) => {
      survey.value = data
    },
    onFetchError: error => {
      router.push({
        name: 'invalid-url',
        params: { code: 404 },
      })
    },
  })
}

const currentTab = ref('CONFIGURATION')

const loading = ref(false)

const form = ref({})
const refForm = ref()

const {
  data: units,
  isFetching: loadingUnits,
} = useApi(createUrl('/masters/units'))

const submitForm = async () => {
  loading.value = true

  refForm.value?.validate().then(({ valid }) => {
    if (valid) {
      const formData = {
        type: surveyType.value,
        ...form.value,
      }

      if (surveyId.value) {
        SurveyService.updateSurvey(
          surveyId.value,
          formData,
          data  => {
            survey.value.status = data.status
          },
          () => {},
        )
      } else {
        SurveyService.createSurvey(
          formData,
          data  => {
            router.push({
              name: 'app-survey-customer-setup',
              params: { 
                type: surveyType.value,
                survey: data.id, 
              },
            })

            survey.value = data
          },
          () => {},
        )
      }
    }
  })

  loading.value = false
}

const scheduleFrequency = await getEnumValue("ScheduleFrequency", true)

const { 
  data: notificationTemplates,
  isFetching: loadingNotificationTemplates,
} = await useApi(
  createUrl(`/masters/notification-templates`,
    {
      query: {
        notification_action: 'survey',
      },
    },
  ),
)

const scheduleForm = ref({})
const refScheduleForm = ref()

const submitScheduleForm = () => {
  refScheduleForm.value?.validate().then(({ valid }) => {
    if (valid) {
      if (scheduleForm.value.id) {
        SurveyService.updateSurveySchedule(
          surveyId.value,
          scheduleForm.value.id,
          scheduleForm.value,
          () => {},
          () => {},
        )
      } else {
        SurveyService.createSurveySchedule(
          surveyId.value,
          scheduleForm.value,
          () => {},
          () => {},
        )
      } 
    }
  })
}

// Delete Survey
const isDeleteDialogVisible = ref(false)

const onDeleteSurvey = confirmed => {
  if (!confirmed) return

  SurveyService.destroySurvey(
    surveyId.value,
    () => {
      if (surveyType.value === SurveyType.CUSTOMER) {
        router.push({ name: 'app-survey-customer' })
      } else if (surveyType.value === SurveyType.EVENT) {
        router.push({ name: 'app-survey-event' })
      } else if (surveyType.value === SurveyType.EMPLOYEE) {
        router.push({ name: 'app-survey-employee' })
      }
    },
    () => {},
  )
}

// Set Form Data
watch(() => survey.value, value => {
  if (value) {
    form.value = {
      title: value.title,
      description: value.description,
      unit_ids: value.units.map(x => x.id),
      status: value.status == 1 ? true : false,
    }

    if (
      (surveyType.value === SurveyType.EMPLOYEE || surveyType.value === SurveyType.EVENT) && value.schedules?.length > 0
    ) {
      scheduleForm.value = value.schedules[0]
      scheduleForm.value.notification_template_ids = value.schedules[0]?.notification_templates?.map(x => x.id) || []
    }
  }
}, {
  immediate: true,  
})
</script>

<template>
  <VAlert
    v-if="surveyId && survey.participant_conditions?.length === 0"
    color="warning"
    icon="tabler-alert-triangle"
    variant="tonal"
    class="mb-6"
  >
    Configure participant conditions before activate the survey.
  </VAlert>

  <VAlert
    v-if="surveyId && !survey.status"
    color="primary"
    icon="tabler-info-circle"
    variant="tonal"
    class="mb-6"
    closable
  >
    Survey is now disabled. Activate the survey to start collecting responses.
  </VAlert>

  <VTabs v-model="currentTab">
    <VTab value="CONFIGURATION">
      Configuration
    </VTab>
    <VTab
      value="QUESTIONNAIRE"
      :disabled="!surveyId"
    >
      Questionnaire
    </VTab>
    <VTab
      v-if="surveyType === SurveyType.CUSTOMER"
      value="PARTICIPANT_CONDITIONS"
      :disabled="!surveyId"
    >
      Participant Conditions
    </VTab>
    <VTab
      v-if="surveyType === SurveyType.EMPLOYEE || surveyType === SurveyType.EVENT" 
      value="PARTICIPANTS"
      :disabled="!surveyId"
    >
      Participants
    </VTab>
  </VTabs>

  <VWindow
    v-model="currentTab"
    class="pt-6 pb-6"
  >
    <VWindowItem value="CONFIGURATION">
      <VRow>
        <VCol 
          md="5" 
          cols="12"
        >
          <VCard
            elevation="0"
            class="border-sm"
          >
            <VCardText>
              <div class="text-h5">
                Basic Information
              </div>
              <div class="text-body-2">
                Provide the basic information for the survey
              </div>
            </VCardText>
            <VCardText>
              <VForm ref="refForm">
                <div class="d-flex flex-column gap-4">
                  <AppTextField
                    v-model="form.title"
                    label="Title"
                    placeholder="Enter title"
                    :rules="[requiredValidator]"
                    :required="true"
                  />

                  <AppTextarea
                    v-model="form.description"
                    rows="3"
                    label="Description"
                    placeholder="Write Description"
                    :rules="[requiredValidator]"
                    :required="true"
                  />

                  <AppSelect
                    v-model="form.unit_ids"
                    :items="units ?? []"
                    :item-title="item => item.name"
                    :item-value="item => item.id"
                    label="Units"
                    placeholder="Select Units"
                    :multiple="true"
                    :loading="loadingUnits"
                    :rules="[requiredValidator]"
                    :required="true"
                  />

                  <VSwitch
                    v-model="form.status"
                    :label="form.status ? 'Active' : 'Inactive'"
                  />

                  <VBtn
                    class="mt-2"
                    color="success"
                    :loading="loading"
                    @click="submitForm"
                  >
                    Save
                  </VBtn>
                </div>
              </VForm>
            </VCardText>

            <!-- Delete Survey -->
            <template v-if="surveyId">
              <VDivider />
              <VCardText>
                <VAlert
                  color="error"
                  variant="tonal"
                >
                  <template #title>
                    <span class="text-body-1 font-weight-medium">Delete Survey</span>
                  </template>
                  <template #text>
                    <p class="text-body-2">
                      Permanently delete this survey and all its associated data including schedules, subscriptions, and responses. This action cannot be undone.
                    </p>
                    <VBtn
                      color="error"
                      variant="elevated"
                      size="small"
                      :disabled="!can('delete', 'survey.survey')"
                      @click="isDeleteDialogVisible = true"
                    >
                      Delete Survey
                    </VBtn>
                  </template>
                </VAlert>
              </VCardText>
            </template>
          </VCard>

          <!-- Delete Confirmation Dialog -->
          <ConfirmDialog
            v-model:is-dialog-visible="isDeleteDialogVisible"
            confirmation-question="Are you sure you want to delete this survey? This will permanently remove the survey and all associated data."
            confirm-title="Deleted!"
            confirm-msg="The survey has been deleted successfully."
            @confirm="onDeleteSurvey"
          />
        </VCol>

        <VCol v-if="surveyType === SurveyType.CUSTOMER">
          <Schedule
            v-if="surveyId"
            :survey="survey"
          />
          <VAlert
            v-else
            color="primary"
            icon="tabler-info-circle"
            variant="tonal"
            class="mb-6"
          >
            Create survey to configure schedules.
          </VAlert>
        </VCol>
        <VCol v-if="surveyType === SurveyType.EMPLOYEE || surveyType === SurveyType.EVENT">
          <VCard
            v-if="surveyId"
            elevation="0"
            class="border-sm"
          >
            <VCardText>
              <div class="d-flex align-center">
                <div>
                  <div class="text-h5">
                    Schedule
                  </div>
                  <div class="text-body-2">
                    Configure schedule for the survey
                  </div>
                </div>
              </div>
            </VCardText>
            <VCardText>
              <VForm
                ref="refScheduleForm"
                class="d-flex flex-column gap-4"
              >
                <AppSelect
                  v-if="surveyType === SurveyType.EMPLOYEE"
                  v-model="scheduleForm.frequency"
                  :items="scheduleFrequency ?? []"
                  :item-title="item => item.title"
                  :item-value="item => item.value"
                  label="Frequency"
                  placeholder="Select Frequency"
                  :rules="[requiredValidator]"
                  :required="true"
                />
                <AppDateTimePicker
                  v-else-if="surveyType === SurveyType.EVENT"
                  v-model="scheduleForm.schedule_at"
                  label="Survey Date Time"
                  placeholder="Enter Date Time"
                />

                <AppSelect
                  v-model="scheduleForm.notification_template_ids"
                  :items="notificationTemplates"
                  :item-title="item => `${item.key} - ${item.notification_channel}`"
                  :item-value="item => item.id"
                  label="Unit Wise Templates"
                  placeholder="Select Unit Wise Templates"
                  :multiple="true"
                  :loading="loadingNotificationTemplates"
                  :rules="[requiredValidator]"
                  :required="true"
                />

                <VBtn
                  class="mt-2"
                  color="success"
                  @click="submitScheduleForm"
                >
                  Save
                </VBtn>
              </VForm>
            </VCardText>
          </VCard>
          <VAlert
            v-else
            color="primary"
            icon="tabler-info-circle"
            variant="tonal"
            class="mb-6"
          >
            Create survey to configure schedule.
          </VAlert>
        </VCol>
      </VRow>
    </VWindowItem>

    <VWindowItem value="QUESTIONNAIRE">
      <Questionnaire :survey-id="surveyId" />
    </VWindowItem>

    <VWindowItem value="PARTICIPANT_CONDITIONS">
      <ParticipantCondition :survey="survey" />
    </VWindowItem>

    <VWindowItem value="PARTICIPANTS">
      <Participants :survey-data="survey" />
    </VWindowItem>
  </VWindow>
</template>
